import UserQ from '@/queries/UserQ'
import { joinSQLFragments, SQL } from '@/client/query'

import { LevelRequirementStatus } from './enum'
import { assertRequirementType } from './helpers'
import type { LevelRequirementQueryFunction, LevelRequirementType } from './types'

// WARNING: If you change the final requirement of a level, you also need to update
// what events we listen to for leveling users up in
// `webapp/src/events/listeners/worlds/progression.ts`

export const LEVEL_REQUIREMENT_TO_QUERY_FUNCTION: Record<
  LevelRequirementType,
  LevelRequirementQueryFunction
> = {
  'complete-training': ({ userId, requirement }) => {
    assertRequirementType(requirement, 'complete-training')
    return SQL<LevelRequirementStatus>`
        (
        CASE WHEN ${UserQ.hasCompletedTraining({ userId, slug: requirement.slug })}
            THEN ${LevelRequirementStatus.Complete}
            ELSE ${LevelRequirementStatus.Incomplete}
            END
        )
    `
  },

  todo: ({ userId, worldSlug, requirement }) => {
    assertRequirementType(requirement, 'todo')
    const { getLevelStatusQueryFunction } = requirement

    // We should never run into a scenario where worldSlug is null but the requirement needs it. Still, if worldSlug is null, requirements that need worldSlug will return false since we are passing ''
    return getLevelStatusQueryFunction({ userId, worldSlug: worldSlug ?? '' })
  },

  'requirement-group': ({ userId, worldSlug, requirement }) => {
    assertRequirementType(requirement, 'requirement-group')
    const { requirements, requireAll } = requirement

    const requirementQueries = requirements.map(requirementItem => {
      const requirementQuery = LEVEL_REQUIREMENT_TO_QUERY_FUNCTION[requirementItem.type]({
        userId,
        worldSlug,
        requirement: requirementItem,
      })
      return SQL<number>`(SELECT
        (CASE
          WHEN (SELECT ${requirementQuery}) = ${LevelRequirementStatus.Complete} THEN 1
          ${
            requireAll
              ? SQL`WHEN (SELECT ${requirementQuery}) = ${LevelRequirementStatus.InProgress} THEN 0.5`
              : SQL``
          }
          ELSE 0
         END
        ))
        `
    })

    const joinedQueries = joinSQLFragments(requirementQueries, ' + ')
    return SQL`
      (CASE
        ${
          requireAll
            ? SQL`
                WHEN completions_sum = ${requirements.length} THEN ${LevelRequirementStatus.Complete}
                WHEN completions_sum > 0 THEN ${LevelRequirementStatus.InProgress}
              `
            : SQL`WHEN completions_sum > 0 THEN ${LevelRequirementStatus.Complete}`
        }
        ELSE ${LevelRequirementStatus.Incomplete}
      END
      )
      FROM (SELECT ${joinedQueries} AS completions_sum) AS subquery
    `
  },
}
